import type { OrganizationDetails } from "~vue/appTypes";
import BasePopover from "~vue/components/BasePopover.vue";
import escapeRegExp from "lodash/escapeRegExp";
import uniqBy from "lodash/uniqBy";
import type { Hooks } from "marked";
import { computed, type ComputedRef, getCurrentInstance, h, inject, nextTick, onBeforeUnmount, type Ref, render, type VNode, watch } from "vue";

import type { ChatAction, PluckedAction } from "./chatActions";
import ChatMyPeopleAccordion from "./ChatDetail/ChatMyPeopleAccordion.vue";
import ChatPersonCard from "./ChatDetail/ChatPersonCard.vue";
import ChatPersonMention, { type PersonMentionVariant } from "./ChatDetail/ChatPersonMention.vue";
import ChatTeamMention from "./ChatDetail/ChatTeamMention.vue";
import { isUnconfirmedTaggedPerson, taggedPeopleKey } from "./ChatDetail/taggedPerson";
import { taggableTeamsKey, teamNamePattern } from "./ChatDetail/taggedTeam";
import { useActionHandler } from "./composables/useActionHandler";
import { useNadiaChatRefreshEnabled } from "./composables/useNadiaChatRefresh";
import type { ConnectionProp } from "./types/connection";

export function useConnectionPopovers(
    hooks: Ref<Hooks>,
    componentId: string,
    connectionChatMessageActions: ComputedRef<PluckedAction<ChatAction.TAGGED_PERSON>[]>,
    teamChatMessageActions: ComputedRef<PluckedAction<ChatAction.TAGGED_TEAM>[]> = computed(() => []),
) {
    const taggableTeams = inject(
        taggableTeamsKey,
        computed(() => []),
    );
    const taggedPeople = inject(taggedPeopleKey, connectionChatMessageActions);
    const appContext = getCurrentInstance()?.appContext;
    const globalProperties = inject("globalProperties");
    const updateActionState = inject("updateActionState");
    const mountedNodes = new Set<HTMLElement>();
    let disposed = false;
    const renderPopover = (vnode: VNode, node: HTMLElement) => {
        if (!mountedNodes.has(node)) {
            node.replaceChildren();
        }
        if (appContext) {
            vnode.appContext = { ...appContext, provides: { ...appContext.provides, globalProperties, updateActionState } };
        }
        render(vnode, node);
        mountedNodes.add(node);
    };
    onBeforeUnmount(() => {
        disposed = true;
        mountedNodes.forEach((node) => render(null, node));
        mountedNodes.clear();
    });
    const sendMessage = inject<(content: string, options?: Record<string, unknown>) => boolean>("sendMessage", () => false);
    const connections = inject<ComputedRef<ConnectionProp[]>>(
        "connections",
        computed(() => []),
    );
    const coachingSessionUuid = inject<string>("coachingSessionUuid", "");
    const publicProfileEnabled = inject<boolean>("publicProfileEnabled", false);
    const collaborationAnalysisEnabled = inject<boolean>("collaborationAnalysisEnabled", false);
    const collaborationAnalysisBusy = inject<ComputedRef<boolean>>(
        "collaborationAnalysisBusy",
        computed(() => false),
    );
    const collaborationExploredConnections = inject<ComputedRef<Set<string>>>(
        "collaborationExploredConnections",
        computed(() => new Set<string>()),
    );
    // Standalone popover roots need page-level context forwarded explicitly.
    const organizationDetails = inject<ComputedRef<OrganizationDetails | null>>(
        "organization_details",
        computed(() => ({})),
    );
    const collaborationProfileInviteUrl = inject<string>("collaborationProfileInviteUrl", "");
    const showChatToast = inject<(message: string) => void>("showChatToast", () => {});
    const { execute } = useActionHandler();
    const isNadiaChatRefreshEnabled = useNadiaChatRefreshEnabled();

    const renderConnectionPopover = async () => {
        await nextTick(() => {
            if (disposed) {
                return;
            }
            for (const node of mountedNodes) {
                if (!node.isConnected) {
                    render(null, node);
                    mountedNodes.delete(node);
                }
            }
            const connectionNodes = document.querySelectorAll(`[data-message-component-id="${componentId}"] [data-connection-id]`);
            connectionNodes.forEach((node) => {
                if (node instanceof HTMLElement === false) {
                    return;
                }
                const connection = connections.value.find((conn) => conn.id === node.dataset.connectionId);
                if (!connection && !isNadiaChatRefreshEnabled.value) {
                    return;
                }

                const mentionAction = connectionChatMessageActions.value.find((action) => action.line.action_params.connection === node.dataset.connectionId);
                const creationAction = taggedPeople.value.find(
                    (action) => action.line.action_params.connection === node.dataset.connectionId && action.line.action_params.was_created,
                );
                // Assistant tags refer to an existing DB row even before the user has pressed Add.
                const actionEntry =
                    isNadiaChatRefreshEnabled.value && creationAction && (isUnconfirmedTaggedPerson(creationAction) || creationAction.line.action_state?.dismissed)
                        ? creationAction
                        : mentionAction;
                const matchText = node.dataset.mentionLabel ?? mentionAction?.line.action_params.match_text ?? connection?.name ?? "";

                if (connection?.declined_at || actionEntry?.line.action_state?.dismissed) {
                    renderPopover(h("span", matchText), node);
                    return;
                }

                if (isNadiaChatRefreshEnabled.value) {
                    const unconfirmed = !!actionEntry && isUnconfirmedTaggedPerson(actionEntry);
                    const hasCollaborationProfile = !!connection?.public_profile;
                    const variant: PersonMentionVariant = hasCollaborationProfile ? "linked" : unconfirmed ? "unmatched" : "suggested";
                    renderPopover(
                        h(
                            ChatPersonMention,
                            { label: matchText, variant, align: mentionAction?.message.role === "user" ? "end" : "start" },
                            () =>
                                actionEntry &&
                                h(ChatPersonCard, {
                                    connectionChatMessageAction: actionEntry,
                                    execute,
                                    csUuid: coachingSessionUuid,
                                    sendMessage,
                                    collaborationAnalysisEnabled,
                                    collaborationAnalysisBusy,
                                    collaborationExploredConnections,
                                    connections,
                                    publicProfileEnabled,
                                    collaborationProfileInviteUrl,
                                    showToast: showChatToast,
                                    logViewOnMount: true,
                                }),
                        ),
                        node,
                    );
                    return;
                }

                renderPopover(
                    h(
                        BasePopover,
                        {
                            sideOffset: 10,
                            collisionPadding: 16,
                        },
                        {
                            trigger: () =>
                                h(
                                    "span",
                                    {
                                        class: "underline decoration-1 underline-offset-2 text-button-tertiary-text cursor-pointer",
                                    },
                                    matchText,
                                ),
                            content: () => {
                                if (!actionEntry) {
                                    return;
                                }
                                return h(ChatMyPeopleAccordion, {
                                    class: "w-80",
                                    connectionChatMessageAction: actionEntry,
                                    execute,
                                    sendMessage,
                                    coachingSessionUuid,
                                    connections,
                                    publicProfileEnabled,
                                    collaborationAnalysisEnabled,
                                    collaborationAnalysisBusy,
                                    collaborationExploredConnections: collaborationExploredConnections.value,
                                    organizationDetails,
                                    collaborationProfileInviteUrl,
                                    showToast: showChatToast,
                                });
                            },
                        },
                    ),
                    node,
                );
            });
            const teamNodes = document.querySelectorAll(`[data-message-component-id="${componentId}"] [data-team-id]`);
            teamNodes.forEach((node) => {
                if (!(node instanceof HTMLElement)) {
                    return;
                }
                const team = taggableTeams.value.find((team) => team.id === node.dataset.teamId);
                if (!team) {
                    return;
                }
                renderPopover(h(ChatTeamMention, { teamId: team.id, label: node.dataset.mentionLabel ?? team.name, teamName: team.name, href: team.url }), node);
            });
        });
    };

    const matchingMentions = computed(() =>
        [
            ...uniqBy(connectionChatMessageActions.value, "line.action_params.connection")
                .filter(({ line }) => {
                    const connection = connections.value.find((conn) => conn.id === line.action_params.connection);
                    return line.action_params.match_text && (isNadiaChatRefreshEnabled.value || (connection && !connection.declined_at));
                })
                .map(({ line }) => ({
                    text: line.action_params.match_text,
                    id: line.action_params.connection,
                    kind: "person" as const,
                    label: line.action_params.match_text,
                    labelOffset: 0,
                })),
            ...uniqBy(teamChatMessageActions.value, "line.action_params.team").flatMap(({ line }) => {
                const text = line.action_params.match_text;
                const name = taggableTeams.value.find((team) => team.id === line.action_params.team)?.name.trim();
                if (line.action_state?.dismissed || !text || !name) {
                    return [];
                }
                // The saved span can include surrounding words to locate a repeated name.
                const matches = [...text.matchAll(teamNamePattern(name))];
                if (matches.length !== 1) {
                    return [];
                }
                return [{ text, id: line.action_params.team, kind: "team" as const, label: matches[0][0], labelOffset: matches[0].index }];
            }),
        ].sort((a, b) => Number(b.kind === "team") - Number(a.kind === "team") || b.text.length - a.text.length),
    );
    // Keep the markdown stable when only confirmation/profile state changes, so open forms retain focus.
    const matchingSignature = computed(() => JSON.stringify({ refresh: isNadiaChatRefreshEnabled.value, mentions: matchingMentions.value }));

    watch(
        matchingSignature,
        async () => {
            const refresh = isNadiaChatRefreshEnabled.value;
            const mentions = matchingMentions.value.map(({ text, id, kind, label, labelOffset }) => ({
                text,
                regex: new RegExp(`(?<![\\p{L}\\p{N}\\p{M}_])${escapeRegExp(text)}(?![\\p{L}\\p{N}\\p{M}_])`, "gu"),
                id,
                kind,
                label,
                labelOffset,
            }));
            hooks.value.postprocess = (html) => {
                if (mentions.length === 0) {
                    return html;
                }
                const template = document.createElement("template");
                template.innerHTML = html;
                const ambiguousTeamTexts = new Set<string>();
                // Team spans identify a single occurrence and must reserve it before
                // the legacy person matcher links other occurrences of the same name.
                for (const { text, regex, id, kind, label, labelOffset } of mentions) {
                    if (kind === "person" && ambiguousTeamTexts.has(text)) {
                        continue;
                    }
                    const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
                    const textNodes: Text[] = [];
                    while (walker.nextNode()) {
                        const node = walker.currentNode as Text;
                        if (!node.parentElement?.closest("a, button, code, pre, script, style, [data-connection-id], [data-team-id]")) {
                            textNodes.push(node);
                        }
                    }
                    if (kind === "team" && textNodes.reduce((count, node) => count + [...node.data.matchAll(regex)].length, 0) !== 1) {
                        ambiguousTeamTexts.add(text);
                        continue;
                    }
                    for (const node of textNodes) {
                        let matched = false;
                        const fragment = document.createDocumentFragment();
                        let end = 0;
                        for (const match of node.data.matchAll(regex)) {
                            const labelStart = match.index + labelOffset;
                            fragment.append(node.data.slice(end, labelStart));
                            const span = document.createElement("span");
                            if (kind === "team") {
                                span.dataset.teamId = id ?? "";
                            } else {
                                span.dataset.connectionId = id ?? "";
                            }
                            span.dataset.mentionLabel = label;
                            span.textContent = label;
                            fragment.append(span);
                            end = labelStart + label.length;
                            matched = true;
                            if (!refresh || kind === "team") {
                                break;
                            }
                        }
                        if (matched) {
                            fragment.append(node.data.slice(end));
                            node.replaceWith(fragment);
                            if (!refresh || kind === "team") {
                                break;
                            }
                        }
                    }
                }
                void renderConnectionPopover();
                return template.innerHTML;
            };
            await renderConnectionPopover();
        },
        { immediate: true },
    );

    watch(
        [connections, connectionChatMessageActions, taggedPeople, taggableTeams, teamChatMessageActions],
        async () => {
            await renderConnectionPopover();
        },
        { immediate: true, deep: true },
    );
}
